import React from 'react';
import Header from './Header';
import Footer from './Footer';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-offWhite text-charcoal">
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-playfair font-bold mb-8 text-charcoal">Terms of Service</h1>
        
        <div className="prose prose-lg max-w-none text-charcoal/80">
          <p className="text-lg mb-6">
            <strong>Last Updated: April 2026</strong>
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">1. Agreement to Terms</h2>
            <p className="mb-4">
              By accessing and using Wako ("Service"), a premium dating and matchmaking platform for singles seeking meaningful connections, you accept and agree to be bound by and comply with these Terms of Service ("Terms") and our Privacy Policy. 
            </p>
            <p className="mb-4">
              Wako is intended for individuals 18 years of age or older. If you do not meet this age requirement or are not authorized to use this Service, you are not permitted to create an account.
            </p>
            <p>
              We reserve the right to modify these Terms at any time. Your continued use of the Service following the posting of revised Terms means that you accept and agree to the changes. We recommend that you check these Terms periodically for updates.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">2. Use License & Eligibility</h2>
            <p className="mb-4">
              Permission is granted to use Wako exclusively for lawful purposes related to dating and finding meaningful connections. You must be at least 18 years old and maintain only one active account. This is a license, not a transfer of title, and under this license you may not:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Attempt to catfish, deceive, or mislead other users about your identity or intentions</li>
              <li>Create fake profiles, use another person's photos, or impersonate someone</li>
              <li>Use the Service for spam, commercial solicitation, or harassment</li>
              <li>Upload explicit, pornographic, or non-consensual intimate images</li>
              <li>Engage in illegal activities or facilitate illegal transactions</li>
              <li>Attempt to gain unauthorized access to accounts or systems</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Sell, trade, or transfer your account to another user</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">3. Disclaimer</h2>
            <p className="mb-4">
              The materials on Wako are provided on an 'as is' basis. Wako makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">4. Limitations</h2>
            <p>
              In no event shall Wako or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Wako, even if Wako or an authorized representative of Wako has been notified orally or in writing of the possibility of such damage.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">5. Accuracy of Materials</h2>
            <p>
              The materials appearing on Wako could include technical, typographical, or photographic errors. Wako does not warrant that any of the materials on its Service are accurate, complete, or current. Wako may make changes to the materials contained on its Service at any time without notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">6. Links</h2>
            <p>
              Wako has not reviewed all of the sites linked to its Service and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by Wako of the site. Use of any such linked website is at the user's own risk.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">7. Modifications</h2>
            <p>
              Wako may revise these Terms of Service for its Service at any time without notice. By using this Service, you are agreeing to be bound by the then current version of these Terms of Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">8. Governing Law</h2>
            <p>
              These Terms and Conditions are governed by and construed in accordance with the laws of [Your Jurisdiction] and you irrevocably submit to the exclusive jurisdiction of the courts in that location.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">9. User Accounts & Conduct</h2>
            <p className="mb-4">
              When you create a Wako account, you are responsible for:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Providing accurate, truthful, and complete profile information</li>
              <li>Maintaining confidentiality of your account credentials</li>
              <li>Accepting responsibility for all activities under your account</li>
              <li>Using recent, genuine photos that represent your appearance</li>
              <li>Notifying us immediately of unauthorized account access</li>
            </ul>
            <p className="mb-4">
              You agree to treat all users with respect and to never:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Harass, threaten, intimidate, or discriminate against other users</li>
              <li>Send unsolicited explicit messages or images</li>
              <li>Attempt to meet someone from Wako in unsafe conditions</li>
              <li>Engage in any form of abuse or threatening behavior</li>
              <li>Share another user's personal information without consent</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">10. Profile Content & Photos</h2>
            <p className="mb-4">
              You retain all rights to any content you submit to your profile ("User Content"), but you grant Wako a worldwide, non-exclusive, royalty-free license to use your profile information for matchmaking, displaying in search results, and improving our Service.
            </p>
            <p className="mb-4">
              Profile Photos & Guidelines:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Photos must be recent, genuine, and represent your actual appearance</li>
              <li>Photos must not contain nudity, explicit content, or offensive material</li>
              <li>Photos must not include other people's faces without their consent</li>
              <li>Photos must not include branding, logos, or promotional material</li>
              <li>We reserve the right to remove inappropriate photos</li>
            </ul>
            <p>
              You represent and warrant that you own or have necessary rights to all User Content and that it complies with all applicable laws and these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">11. Prohibited Conduct & Safety</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for predatory, abusive, or exploitative purposes</li>
              <li>Engage in romantic scams or financial manipulation</li>
              <li>Arrange meetings for illegal activities or sex trafficking</li>
              <li>Sexually harass or send unsolicited explicit content</li>
              <li>Violate anyone's consent, privacy, or safety</li>
              <li>Attempt to gain unauthorized access or hack accounts</li>
              <li>Upload malicious code or interfere with Service functionality</li>
              <li>Misrepresent your identity, age, or intentions</li>
              <li>Collect personal information for unlawful purposes</li>
              <li>Violate any applicable laws, including those against discrimination</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">12. Intellectual Property Rights</h2>
            <p className="mb-4">
              The Service and its entire contents, features, and functionality (including but not limited to all information, software, text, displays, images, video, and audio) are owned by Wako or its content suppliers and are protected by United States and international copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">13. Termination</h2>
            <p>
              Wako reserves the right to terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason whatsoever, including if you breach any terms or conditions of these Terms of Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">14. Premium Subscription Terms</h2>
            <p className="mb-4">
              Wako offers premium subscription plans with additional features. By purchasing a premium subscription, you agree to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Pay the stated subscription fee on a recurring basis (monthly or annually)</li>
              <li>Automatic renewal unless you cancel before the renewal date</li>
              <li>Full refund eligibility within 24 hours of purchase (platform-specific policies may vary)</li>
              <li>Changes to premium features may occur, but core features will remain available</li>
              <li>Cancellation instructions available in your account settings</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">15. Safety Disclaimer</h2>
            <p className="mb-4">
              While Wako implements safety measures to protect users, we cannot guarantee your safety. We strongly recommend:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Meeting in public places only, especially on first meetings</li>
              <li>Telling trusted friends or family about your plans</li>
              <li>Verifying user information before meeting in person</li>
              <li>Avoiding sharing sensitive personal or financial information</li>
              <li>Trusting your instincts and leaving uncomfortable situations</li>
            </ul>
            <p>
              Report any suspicious activity, safety concerns, or policy violations to our support team immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">16. Contact Us</h2>
            <p>
              If you have questions about these Terms of Service or need to report safety concerns, please contact us.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
